import { useState, useEffect } from "react";

interface TocItem {
  id: string;
  title: string;
  level: number;
}

interface TableOfContentsProps {
  content: string;
  readingTime: number;
  wordCount: number;
}

const TableOfContents = ({ content, readingTime, wordCount }: TableOfContentsProps) => {
  const [activeId, setActiveId] = useState<string>("");
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  // Parse headings from content
  const tocItems: TocItem[] = content
    .split("\n")
    .filter((line) => line.startsWith("## ") || line.startsWith("### "))
    .map((line) => {
      const level = line.startsWith("### ") ? 3 : 2;
      const title = line.replace(/^#{2,3} /, "");
      const id = title
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9\u00C0-\u024F-]/g, "")
        .slice(0, 50);
      return { id, title, level };
    });

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? Math.min(100, (scrollTop / docHeight) * 100) : 0;
      setScrollProgress(progress);

      // Find active heading
      const headings = document.querySelectorAll("h2[data-toc-id]");
      let current = "";
      headings.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.top <= 120) {
          current = el.getAttribute("data-toc-id") || "";
        }
      });
      setActiveId(current);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToHeading = (id: string) => {
    const el = document.querySelector(`h2[data-toc-id="${id}"]`);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  if (tocItems.length === 0) return null;

  return (
    <div className="bg-amber-50 border border-amber-100 rounded-2xl overflow-hidden mb-8">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-amber-100">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg">
            <i className="ri-list-check-2 text-amber-600"></i>
          </div>
          <div>
            <p className="font-bold text-stone-900 text-sm">İçindekiler</p>
            <p className="text-stone-400 text-xs">{tocItems.length} bölüm</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Reading time & word count */}
          <div className="hidden sm:flex items-center gap-3 text-xs text-stone-500">
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-time-line text-amber-500"></i>
              </div>
              <span><strong className="text-stone-700">{readingTime} dk</strong> okuma</span>
            </div>
            <div className="w-px h-3 bg-stone-200"></div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-text text-amber-500"></i>
              </div>
              <span><strong className="text-stone-700">{wordCount.toLocaleString("tr-TR")}</strong> kelime</span>
            </div>
          </div>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="w-7 h-7 flex items-center justify-center bg-amber-100 hover:bg-amber-200 rounded-lg transition-colors cursor-pointer"
          >
            {isCollapsed
              ? <i className="ri-arrow-down-s-line text-amber-600 text-sm"></i>
              : <i className="ri-arrow-up-s-line text-amber-600 text-sm"></i>
            }
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-0.5 bg-amber-100">
        <div
          className="h-full bg-amber-400 transition-all duration-300"
          style={{ width: `${scrollProgress}%` }}
        ></div>
      </div>

      {/* TOC Items */}
      {!isCollapsed && (
        <div className="px-5 py-4 space-y-1">
          {tocItems.map((item, idx) => (
            <button
              key={item.id}
              onClick={() => scrollToHeading(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left transition-all cursor-pointer group ${
                activeId === item.id
                  ? "bg-amber-200/60 text-amber-800"
                  : "hover:bg-amber-100/60 text-stone-600 hover:text-stone-900"
              } ${item.level === 3 ? "pl-8" : ""}`}
            >
              <span className={`text-xs font-bold flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full transition-colors ${
                activeId === item.id ? "bg-amber-500 text-white" : "bg-amber-100 text-amber-600 group-hover:bg-amber-200"
              }`}>
                {idx + 1}
              </span>
              <span className="text-sm leading-snug">{item.title}</span>
              {activeId === item.id && (
                <div className="ml-auto w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className="ri-arrow-right-s-line text-amber-600"></i>
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Mobile reading stats */}
      <div className="sm:hidden flex items-center gap-4 px-5 pb-4 text-xs text-stone-500">
        <div className="flex items-center gap-1">
          <i className="ri-time-line text-amber-500"></i>
          <span><strong className="text-stone-700">{readingTime} dk</strong> okuma</span>
        </div>
        <div className="flex items-center gap-1">
          <i className="ri-text text-amber-500"></i>
          <span><strong className="text-stone-700">{wordCount.toLocaleString("tr-TR")}</strong> kelime</span>
        </div>
      </div>
    </div>
  );
};

export default TableOfContents;
